import { multiplyOnPhoto } from './multiply-on-photo';
import { drawRaster, toPremulCanvas, toStraightRaster } from './premul-canvas';
import { createRaster, type Raster } from './raster';
import type { CylinderPreview } from './warp-cylinder';

const ORIGIN = { x: 0, y: 0 } as const;

/**
 * 면 하나를 합성할 층. 사진·마스크·음영은 같은 캔버스 크기다.
 * - photo: 상품 사진(마플 `cv_bpcf`)
 * - preview: 원통 미리보기(warpCylinder 결과)
 * - multiply: 켜면 미리보기를 사진과 먼저 곱한다(multiplyOnPhoto)
 * - mask: 상품 실루엣 마스크(마플 `cv_mask1`). 없으면 자르지 않는다.
 * - shading: 음영(마플 `cv_shading`). 없으면 얹지 않는다.
 */
export interface FaceLayers {
  readonly photo: Raster;
  readonly preview: CylinderPreview;
  readonly multiply?: boolean;
  readonly mask?: Raster;
  readonly shading?: Raster;
}

/**
 * 면 하나를 마플 미리보기 모드의 층 순서로 합성한다. 캔버스 크기는 사진 크기다.
 * 1. 미리보기를 배치 left·top 을 내림한 정수 위치에 source-over 로 놓는다.
 * 2. 마스크를 destination-in 으로 그린다. 사진을 그리기 전이라 사진은 잘리지 않는다.
 * 3. 사진을 destination-over 로 그린다.
 * 4. 음영을 source-over 로 얹는다.
 * 층마다 premultiplied 8비트로 저장한다. 마플은 미리보기를 소수 위치·소수 폭으로 다시 그리지만 여기서는 원래 크기로 정수 위치에 놓는다.
 * 마스크나 음영 크기가 사진과 다르면 RangeError 다.
 */
export function composeFace(layers: FaceLayers): Raster {
  const { photo, mask, shading } = layers;
  assertCanvasSize(photo, mask, 'mask');
  assertCanvasSize(photo, shading, 'shading');
  const preview = layers.multiply ? multiplyOnPhoto(layers.preview, photo) : layers.preview;
  const at = { x: Math.floor(preview.placement.left), y: Math.floor(preview.placement.top) };
  const blank = toPremulCanvas(createRaster(photo.width, photo.height));
  const withPreview = drawRaster(blank, preview.raster, at, 'source-over');
  const masked = mask ? drawRaster(withPreview, mask, ORIGIN, 'destination-in') : withPreview;
  const withPhoto = drawRaster(masked, photo, ORIGIN, 'destination-over');
  const shaded = shading ? drawRaster(withPhoto, shading, ORIGIN, 'source-over') : withPhoto;
  return toStraightRaster(shaded);
}

function assertCanvasSize(photo: Raster, layer: Raster | undefined, name: string): void {
  if (layer && (layer.width !== photo.width || layer.height !== photo.height)) {
    throw new RangeError(
      `${name} 크기 ${layer.width}×${layer.height} 가 사진 크기 ${photo.width}×${photo.height} 와 다르다`,
    );
  }
}
